import type { LobbyState } from "@genjiball/protocol";

export interface HudHandlers {
  onReadyToggle: (ready: boolean) => void;
  onStart: () => void;
  onCopyLink: () => void;
  onAddBot: () => void;
}

export class LobbyHud {
  private root: HTMLElement;
  private playersEl: HTMLElement;
  private readyBtn: HTMLButtonElement;
  private startBtn: HTMLButtonElement;
  private copyBtn: HTMLButtonElement;
  private addBotBtn: HTMLButtonElement;
  private noticeEl: HTMLElement;
  private myId: string | null = null;
  private currentReady: boolean = false;

  constructor(private handlers: HudHandlers) {
    this.root = document.getElementById("hud")!;
    this.root.innerHTML = `
      <div class="hud-panel hud-players">
        <h3>Players</h3>
        <ul id="hud-players-list"></ul>
      </div>
      <div class="hud-bottom">
        <button id="hud-copy">Copy invite link</button>
        <button id="hud-ready" class="ready-off">Ready</button>
        <button id="hud-add-bot" hidden>Add Bot</button>
        <button id="hud-start" hidden disabled>Start match</button>
      </div>
      <div id="hud-notice" hidden></div>
    `;
    this.playersEl = this.root.querySelector("#hud-players-list")!;
    this.readyBtn = this.root.querySelector("#hud-ready")!;
    this.startBtn = this.root.querySelector("#hud-start")!;
    this.copyBtn = this.root.querySelector("#hud-copy")!;
    this.addBotBtn = this.root.querySelector("#hud-add-bot")!;
    this.noticeEl = this.root.querySelector("#hud-notice")!;

    this.readyBtn.addEventListener("click", () => {
      this.currentReady = !this.currentReady;
      this.handlers.onReadyToggle(this.currentReady);
    });
    this.startBtn.addEventListener("click", () => this.handlers.onStart());
    this.copyBtn.addEventListener("click", () => this.handlers.onCopyLink());
    this.addBotBtn.addEventListener("click", () => this.handlers.onAddBot());
  }

  setSelf(id: string) {
    this.myId = id;
  }

  render(lobby: LobbyState) {
    const meIsLeader = lobby.leaderId === this.myId;
    this.startBtn.hidden = !meIsLeader;

    const readyCount = Object.values(lobby.players).filter((p) => p.ready && p.connected).length;
    this.startBtn.disabled = readyCount < 2;
    this.startBtn.textContent = meIsLeader ? `Start match (${readyCount} ready)` : "Start match";

    // Add-Bot button: dev builds only, leader only.
    const showBots = import.meta.env.DEV && meIsLeader;
    this.addBotBtn.hidden = !showBots;

    // Player list.
    this.playersEl.innerHTML = "";
    for (const p of Object.values(lobby.players)) {
      const li = document.createElement("li");
      const swatch = document.createElement("span");
      swatch.className = "swatch";
      swatch.style.background = p.color;
      const name = document.createElement("span");
      const baseName = p.name + (p.id === lobby.leaderId ? " 👑" : "") + (p.id === this.myId ? " (you)" : "");
      name.innerHTML = "";
      name.appendChild(document.createTextNode(baseName));
      const sc = document.createElement("span");
      sc.className = "score-num";
      sc.textContent = String(p.score);
      name.appendChild(sc);
      const dot = document.createElement("span");
      dot.className = "ready-dot " + (p.ready ? "on" : "off");
      li.appendChild(swatch);
      li.appendChild(name);
      li.appendChild(dot);
      this.playersEl.appendChild(li);
    }

    // Match-started notice.
    if (lobby.status === "playing") {
      this.noticeEl.hidden = false;
      this.noticeEl.textContent = "Match starting — gameplay arrives in Phase 2.";
    } else {
      this.noticeEl.hidden = true;
    }

    // Sync own ready button state with server state.
    if (this.myId) {
      const me = lobby.players[this.myId];
      if (me) {
        this.currentReady = me.ready;
        this.readyBtn.className = me.ready ? "ready-on" : "ready-off";
        this.readyBtn.textContent = me.ready ? "Unready" : "Ready";
      }
    }
  }
}
